import React, { FC, useEffect, useState } from 'react';
import { Button, Dropdown, Grid, Modal, notification, Table } from 'antd';
import { IGetUsersProps, IUser } from '../admin-users.type';
import { getDeletedUsers, restoreDeletedUser, hardDeleteUser } from '../actions';
import DotsIcon from '../../manage-reestr-item/dots.svg';
import { TablePaginationConfig } from 'antd/lib/table/interface';
import UserSearchFilter from '../search-forms/user-search-filter';
import { localize } from 'mixins/helpers';

const DeletedUserList: FC<IGetUsersProps> = (props: IGetUsersProps) => {
    const { translations } = props;
    const [users, setUsers] = useState<IUser[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isAction, setIsAction] = useState<number>();
    const breakpoint = Grid.useBreakpoint();
    const [pagination, setPagination] = useState<any>({
        current: 1,
        pageSize: 50,
    });
    const [filterData, setFilterData] = useState({});
    const [current, setCurrent] = useState(1);

    const items = [
        {
            label: localize('admin-users-action-restore', translations),
            key: '1',
        },
        {
            label: localize('admin-users-action-hard-delete', translations),
            key: '2',
            danger: true,
        },
    ];

    const columns = [
        {
            title: 'Id',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: localize('fio', translations),
            dataIndex: 'fio',
            key: 'fio',
            render: (_, x) => `${x.lastName} ${x.firstName}`,
        },
        {
            title: localize('email-with-dash', translations),
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: localize('admin-users-email-confirmed', translations),
            dataIndex: 'emailConfirmed',
            key: 'emailConfirmed',
            render: (x) => (x ? localize('yes', translations) : localize('no', translations)),
        },
        {
            title: localize('admin-users-phone', translations),
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
        },
        {
            title: localize('admin-users-phone-confirmed', translations),
            dataIndex: 'phoneConfirmed',
            key: 'phoneConfirmed',
            render: (x) => (x ? localize('yes', translations) : localize('no', translations)),
        },
        {
            title: localize('iin', translations),
            dataIndex: 'taxpayerCode',
            key: 'taxpayerCode',
        },
        {
            title: localize('actions', translations),
            dataIndex: 'id',
            key: 'actions',
            render: (id) => (
                <Dropdown
                    menu={{ items, onClick: ({ key }) => onClick(key, id) }}
                    trigger={['click']}
                >
                    <Button
                        className="p-0"
                        shape="round"
                        icon={<DotsIcon />}
                        loading={isAction === id}
                    />
                </Dropdown>
            ),
        },
    ];

    const restoreUser = (id: number) => {
        Modal.confirm({
            content: localize('admin-users-restore-confirm', translations),
            onOk() {
                setIsAction(id);

                restoreDeletedUser(id)
                    .then(() => {
                        notification['success']({
                            message: localize('admin-users-restore-success', translations),
                        });
                        loadData();
                    })
                    .finally(() => setIsAction(null));
            },
        });
    };

    const hardRemoveUser = (id: number) => {
        Modal.confirm({
            title: 'Удаление пользователя',
            content: 'Вы действительно хотите удалить пользователя навсегда? Это действие нельзя отменить.',
            okText: 'Удалить навсегда',
            okType: 'danger',
            cancelText: 'Отмена',

            async onOk() {
                setIsAction(id);

                try {
                    await hardDeleteUser(id);

                    notification.success({
                        message: 'Пользователь удалён навсегда',
                    });

                    loadData();
                } catch (error) {
                    notification.error({
                        message: 'Не удалось удалить пользователя',
                    });

                    throw error;
                } finally {
                    setIsAction(null);
                }
            },
        });
    };

    const onClick = (key: string, id: number) => {
        switch (key) {
            case '1':
                restoreUser(id);
                break;
            case '2':
                hardRemoveUser(id);
                break;
        }
    };

    const loadData = (values?: any) => {
        setIsLoading(true);

        const cleanData = values || filterData;

        const params = {
            page: pagination.current - 1,
            size: pagination.pageSize,
            sort: 'id,desc',
            ...cleanData,
        };

        getDeletedUsers(params)
            .then((value) => {
                setUsers(value.data.content);
                setPagination({
                    ...pagination,
                    total: value.data.totalElements,
                });
            })
            .finally(() => setIsLoading(false));
    };

    const handleTableChange = (pagination: TablePaginationConfig) => {
        setPagination(pagination);
    };

    useEffect(() => {
        if (props.activeTab === props.tabKey) {
            loadData();
        }
    }, [pagination.current, props.activeTab]);

    const onClear = (values = {}) => {
        setFilterData(values);

        if (current > 1) {
            setCurrent(1);
        } else {
            loadData(values);
        }
    };

    return (
        <>
            {props.activeTab === props.tabKey && (
                <UserSearchFilter
                    {...props}
                    onSearch={loadData}
                    onClear={onClear}
                />
            )}
            <Table
                columns={columns}
                dataSource={users}
                className="my-3"
                loading={isLoading}
                pagination={{
                    ...pagination,
                    size: breakpoint.xs ? 'small' : 'default',
                    position: [breakpoint.xs ? 'bottomCenter' : 'bottomRight'],
                }}
                onChange={handleTableChange}
                scroll={{
                    x: true,
                }}
            />
        </>
    );
};

export default DeletedUserList;